import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { MapaPicker } from './MapaPicker';
import { parseUbicacion } from '@shared/ubicacionParser';
import { geocodeInverso } from '@renderer/lib/geocodeInverso';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';

/**
 * Sprint 66 — **¿A dónde se lo llevamos?**
 *
 * Antes esto vivía pegado al costo de envío en un solo paso que iba **después** del menú. El
 * dueño lo puso antes y tenía razón del oficio: *"¿y si vive en un municipio o no hay cobertura?"*.
 * Preguntar la dirección al final significa descubrir que no se llega hasta allá con el pedido
 * entero ya capturado y el cliente al teléfono. Quien contesta el teléfono en un negocio pregunta
 * "¿a dónde es?" en el segundo diez, precisamente para poder decir "hasta allá no llegamos" antes
 * de que nadie pierda cinco minutos.
 *
 * El **costo** del envío sigue después del menú (`CostoEnvioForm`), porque ése sí depende del
 * pedido y de lo que se pacte con el repartidor. Lo que se adelantó es la dirección, no el dinero.
 *
 * ## Sprint 98 — deja de ser una pantalla y pasa a ser una sección
 *
 * Era el paso 2 de cuatro, con su propia ventana y sus propios botones. Reporte del dueño del
 * producto: *"aún lo siento con muchos pasos"*. Y de los cuatro, éste era el único que sobraba:
 * quien contesta el teléfono pregunta **"¿quién habla y a dónde es?"** en la misma respiración —
 * son una sola pregunta, no dos pantallas.
 *
 * Ahora vive dentro de `QuienYADondeForm`, controlado desde arriba: aquí quedan los campos, el
 * mapa y la lectura de enlaces de WhatsApp; la validación y el "continuar" son del formulario que
 * lo contiene. La garantía del Sprint 66 se conserva —la dirección se sigue pidiendo **antes** del
 * menú—, sólo que ya no cuesta una pantalla extra.
 *
 * El semáforo de cobertura (verde/ámbar/rojo con su cuota) queda pendiente de que la dueña diga
 * cuánto cobra y hasta dónde llega — DT-059.
 */
export interface EntregaInfo {
  direccion: string;
  latitud?: number;
  longitud?: number;
  referenciasEntrega?: string;
}

interface Props {
  valor: EntregaInfo;
  onCambio: (v: EntregaInfo) => void;
}

export function SeccionDondeEntregar({ valor, onCambio }: Props) {
  const [mapaAbierto, setMapaAbierto] = useState(false);
  /**
   * Sprint 65 — Estado de "estoy leyendo lo que pegaste".
   *
   * El operador recibe el enlace por WhatsApp y lo pega en el campo grande, que es lo obvio.
   * Antes eso se guardaba como dirección textual: el repartidor recibía una URL y el mapa del
   * ticket aterrizaba en cualquier parte, porque Google buscaba la URL como si fuera una calle.
   */
  const [leyendoEnlace, setLeyendoEnlace] = useState(false);
  const [avisoUbicacion, setAvisoUbicacion] = useState<string | null>(null);
  const [errorUbicacion, setErrorUbicacion] = useState<string | null>(null);

  const { direccion, latitud, longitud, referenciasEntrega } = valor;
  const cambiar = (parcial: Partial<EntregaInfo>) => onCambio({ ...valor, ...parcial });

  const handleUbicacion = (lat: number, lng: number, dir?: string) => {
    cambiar({
      latitud: lat,
      longitud: lng,
      ...(dir && !direccion ? { direccion: dir } : {}),
    });
    setMapaAbierto(false);
  };

  /**
   * Sprint 65 — Lee lo que el operador acaba de pegar en el campo de dirección.
   *
   * Tres casos:
   *  - **Enlace corto de Google Maps** (`maps.app.goo.gl/…`, lo que manda WhatsApp): lo resuelve
   *    el proceso main siguiendo el redirect, y de ahí salen las coordenadas exactas del pin.
   *  - **Coordenadas o URL larga**: se leen sin salir a la red.
   *  - **Texto normal**: no se toca. Escribir "Júpiter 206, Galaxia" sigue funcionando igual.
   *
   * En los dos primeros casos la URL **se reemplaza** por la calle real (geocode inverso). Si no
   * hay internet para el nombre de la calle, se deja el texto anterior o las coordenadas: lo que
   * no puede pasar es que la dirección del repartidor sea un enlace.
   */
  const interpretarPegado = async (texto: string) => {
    const r = parseUbicacion(texto);
    if (r.tipo === 'no_reconocido') return;

    setErrorUbicacion(null);
    let coords: { lat: number; lng: number } | null = null;

    if (r.tipo === 'coords') {
      coords = { lat: r.lat, lng: r.lng };
    } else {
      setLeyendoEnlace(true);
      const res = await window.api.ubicacion.resolverEnlace(r.url);
      setLeyendoEnlace(false);
      if (!res.ok) {
        // Se conserva lo pegado para que pueda copiarlo al mapa a mano.
        setErrorUbicacion(
          `${traducirErrorBackend(res.error)} Puedes abrir el mapa y marcar el punto a mano.`,
        );
        return;
      }
      coords = res.data;
    }

    const calle = await geocodeInverso(coords.lat, coords.lng);
    cambiar({
      latitud: coords.lat,
      longitud: coords.lng,
      direccion: calle ?? `Ubicación GPS ${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`,
    });
    setAvisoUbicacion(
      calle
        ? 'Leí el enlace del cliente y puse la dirección. Revísala antes de continuar.'
        : 'Leí el enlace del cliente. No pude obtener el nombre de la calle, pero el repartidor tiene el punto exacto.',
    );
  };

  return (
    <>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between gap-2">
            <label className="label" htmlFor="cliDir">
              Dirección *
            </label>
            {/*
              Sprint 65 — Era un enlace de texto de 12 px, muy por debajo del mínimo de 44 px
              que este proyecto se fijó (Fitts). Con el mapa escondido ahí, la ruta natural
              para una ubicación de WhatsApp era el campo de texto — y de ahí salía el pin
              equivocado.
            */}
            <button
              type="button"
              onClick={() => setMapaAbierto(true)}
              className="flex min-h-[44px] items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-3 text-sm font-medium text-blue-800 hover:bg-blue-100"
            >
              <MapPin className="h-4 w-4" />
              {latitud ? 'Ver o mover en el mapa' : 'Marcar en el mapa'}
            </button>
          </div>
          <textarea
            id="cliDir"
            className="input"
            rows={2}
            value={direccion}
            onChange={(e) => cambiar({ direccion: e.target.value })}
            /*
              Sprint 65 — Se interpreta al PEGAR (el gesto real: llega el enlace por WhatsApp y
              se pega aquí) y también al salir del campo, para quien lo escriba o lo pegue con
              el menú del botón derecho.
            */
            onPaste={(e) => {
              const pegado = e.clipboardData.getData('text');
              if (parseUbicacion(pegado).tipo !== 'no_reconocido') {
                e.preventDefault();
                void interpretarPegado(pegado);
              }
            }}
            onBlur={() => void interpretarPegado(direccion)}
            placeholder="Calle, número, colonia... o pega el enlace de ubicación que mandó el cliente"
          />
          {leyendoEnlace && (
            <div className="mt-1 text-xs text-blue-700">Abriendo el enlace del cliente…</div>
          )}
          {avisoUbicacion && !leyendoEnlace && (
            <div className="mt-1 rounded bg-blue-50 px-2 py-1 text-xs text-blue-800">
              {avisoUbicacion}
            </div>
          )}
          {errorUbicacion && (
            <div className="mt-1 rounded bg-rose-50 px-2 py-1 text-xs text-rose-700" role="alert">
              {errorUbicacion}
            </div>
          )}
          {latitud !== undefined && longitud !== undefined && (
            <div className="mt-1 flex items-center gap-1 text-xs text-emerald-600">
              <MapPin className="h-3 w-3" />
              GPS: {latitud.toFixed(5)}, {longitud.toFixed(5)}
            </div>
          )}
        </div>

        <div>
          <label className="label" htmlFor="cliRef">
            Referencias para el repartidor
          </label>
          <textarea
            id="cliRef"
            className="input"
            rows={2}
            value={referenciasEntrega ?? ''}
            onChange={(e) => cambiar({ referenciasEntrega: e.target.value })}
            placeholder="Casa azul, timbre 2, frente a la tienda..."
          />
        </div>
      </div>

      {mapaAbierto && (
        <MapaPicker
          latInicial={latitud}
          lngInicial={longitud}
          direccionInicial={direccion}
          onConfirmar={handleUbicacion}
          onCerrar={() => setMapaAbierto(false)}
        />
      )}
    </>
  );
}
